import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import type { AffiliateSelection } from "./affiliateSelection";
import {
  readStoredAffiliateId,
  writeStoredAffiliateId,
} from "./affiliateStorage";
import { type AuthState, deriveAuthState } from "./authState";
import { setAuthSnapshot, setReauthenticationHandler } from "./authStore";
import { type SigninState, currentReturnTo } from "./returnTo";

/**
 * Bridges react-oidc-context into the rest of the app.
 *
 * It derives the router {@link AuthState} from the live OIDC session, keeps the
 * framework-agnostic {@link setAuthSnapshot auth store} in sync so the API client
 * can read the current bearer token and affiliate id synchronously without
 * touching React, and registers the reauthentication handler the API client
 * calls on a 401. The returned state is fed into the router context so guards
 * react to sign-in, sign-out and silent renew.
 *
 * It also owns the affiliate *choice*: the persisted selection is folded into
 * the derived state, and {@link AffiliateSelection.selectAffiliate} switches
 * tenants. The returned selection is handed to the `AffiliateSelectionProvider`
 * so the sidebar switcher can drive it.
 */
export function useAuthStoreSync(): {
  authState: AuthState;
  affiliateSelection: AffiliateSelection;
} {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const userId = auth.user?.profile.sub ?? null;

  // Seeded from storage on first render and re-read whenever the user changes,
  // so signing in as someone else never inherits the previous user's choice.
  const [selectedAffiliateId, setSelectedAffiliateId] = useState<string | null>(
    () => readStoredAffiliateId(userId),
  );
  const [storedForUserId, setStoredForUserId] = useState<string | null>(userId);
  if (storedForUserId !== userId) {
    setStoredForUserId(userId);
    setSelectedAffiliateId(readStoredAffiliateId(userId));
  }

  const authState = deriveAuthState(
    {
      isAuthenticated: auth.isAuthenticated,
      isLoading: auth.isLoading,
      user: auth.user,
    },
    selectedAffiliateId,
  );
  const accessToken = auth.user?.access_token ?? null;
  const { affiliateId, availableAffiliateIds } = authState;
  const { signinRedirect } = auth;

  useEffect(() => {
    setAuthSnapshot({ accessToken, affiliateId });
  }, [accessToken, affiliateId]);

  useEffect(() => {
    setReauthenticationHandler(() => {
      void signinRedirect({
        state: { returnTo: currentReturnTo() } satisfies SigninState,
      });
    });
    return () => setReauthenticationHandler(null);
  }, [signinRedirect]);

  const selectAffiliate = useCallback(
    (nextAffiliateId: string): void => {
      // Guard against ids outside the claim: every tenant-scoped request would
      // 403, and the choice would be persisted for the next reload too.
      if (
        !availableAffiliateIds.includes(nextAffiliateId) ||
        nextAffiliateId === affiliateId
      ) {
        return;
      }

      writeStoredAffiliateId(userId, nextAffiliateId);
      // Publish the new tenant to the auth store *before* dropping the cache.
      // The API client reads the affiliate header from that store synchronously
      // per request, while the effect below only runs after this render. Any
      // observer still mounted refetches the moment the cache is cleared, so
      // clearing first would repopulate it with the previous tenant's data.
      setAuthSnapshot({ accessToken, affiliateId: nextAffiliateId });
      setSelectedAffiliateId(nextAffiliateId);
      // Every cached entry was fetched for the previous tenant, so drop the lot
      // rather than allowlisting exceptions: a query added later would otherwise
      // silently serve another affiliate's data. The only refetch this costs is
      // the (root-scoped) affiliate directory.
      queryClient.clear();
    },
    [accessToken, affiliateId, availableAffiliateIds, queryClient, userId],
  );

  // Memoized because this object is the context value: a fresh literal each
  // render would re-render every consumer regardless of the memoized callback.
  const affiliateSelection = useMemo(
    () => ({
      affiliateId,
      availableAffiliateIds,
      canSwitch: availableAffiliateIds.length > 1,
      selectAffiliate,
    }),
    [affiliateId, availableAffiliateIds, selectAffiliate],
  );

  return { authState, affiliateSelection };
}
